/**
 * DateRangePicker Component
 * Date range picker with optional time selection (24h format)
 *
 * Features:
 * - "Box" layout with Header, Body, Footer
 * - View swapping between Calendar and Time Picker
 * - 24-hour time format (HH:mm)
 * - Presets support
 * - Independent navigation for calendar columns
 */

import * as React from "react";
import { format, addMonths, isSameMonth, isBefore, isSameDay } from "date-fns";
import { Calendar as CalendarIcon, Clock, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { TimePicker } from "@/components/ui/time-picker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface DateRangeValue {
  from?: string; // ISO string
  to?: string; // ISO string
}

export interface DateRangePickerProps {
  value?: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  includeTime?: boolean;
  disablePast?: boolean;
  disableFuture?: boolean;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function DateRangePicker({
  value,
  onChange,
  includeTime = false,
  disablePast = false,
  disableFuture = false,
  placeholder = "Select date range",
  className,
  disabled = false,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeView, setActiveView] = React.useState<
    "calendar" | "time-start" | "time-end"
  >("calendar");

  // Helper to parse ISO string to Date
  const parseDate = (isoStr: string | undefined): Date | undefined => {
    if (!isoStr) return undefined;
    const d = new Date(isoStr);
    return isNaN(d.getTime()) ? undefined : d;
  };

  const fromDate = parseDate(value?.from);
  const toDate = parseDate(value?.to);

  // Independent months state
  const [leftMonth, setLeftMonth] = React.useState<Date>(
    fromDate || new Date(),
  );
  const [rightMonth, setRightMonth] = React.useState<Date>(() => {
    const d = toDate || new Date();
    if (!toDate) d.setMonth(d.getMonth() + 1);
    return d;
  });

  // Update months when popover opens or value changes significantly
  React.useEffect(() => {
    if (isOpen) {
      if (fromDate) setLeftMonth(fromDate);
      if (toDate) {
        setRightMonth(toDate);
      } else if (fromDate) {
        const next = new Date(fromDate);
        next.setMonth(next.getMonth() + 1);
        setRightMonth(next);
      } else {
        setLeftMonth(new Date());
        const next = new Date();
        next.setMonth(next.getMonth() + 1);
        setRightMonth(next);
      }
    }
  }, [isOpen, value]);

  // Format helpers
  const formatDate = (date: Date | undefined) => {
    if (!date) return "Select Date";
    return format(date, "yyyy-MM-dd");
  };

  const formatTime = (date: Date | undefined) => {
    if (!date) return "00:00";
    return format(date, "HH:mm");
  };

  // Handlers
  const handleCalendarSelect = (
    range: { from?: Date; to?: Date } | undefined,
    selectedDay: Date,
  ) => {
    // UX Improvement: If a full range is already selected, start a new range from the clicked date
    if (fromDate && toDate && selectedDay) {
      const newFrom = new Date(selectedDay);
      if (fromDate) {
        newFrom.setHours(fromDate.getHours(), fromDate.getMinutes());
      }
      onChange({
        from: newFrom.toISOString(),
        to: undefined,
      });
      return;
    }

    if (!range) {
      onChange({ from: undefined, to: undefined });
      return;
    }

    const newFrom = range.from;
    let newTo = range.to;

    // Fix: If this is the first click (no previous start date) and from/to are the same,
    // force 'to' to be undefined so the user can pick an end date.
    if (!fromDate && newFrom && newTo && isSameDay(newFrom, newTo)) {
      newTo = undefined;
    }

    // Preserve time if exists
    if (fromDate && newFrom) {
      newFrom.setHours(fromDate.getHours(), fromDate.getMinutes());
    }
    if (toDate && newTo) {
      newTo.setHours(toDate.getHours(), toDate.getMinutes());
    }

    onChange({
      from: newFrom?.toISOString(),
      to: newTo?.toISOString(),
    });
  };

  const handleTimeChange = (type: "from" | "to", newDate: Date) => {
    if (type === "from") {
      onChange({ ...value, from: newDate.toISOString() });
    } else {
      onChange({ ...value, to: newDate.toISOString() });
    }
  };

  // Navigation Handlers (prevent overlap)
  const handleLeftMonthChange = (date: Date) => {
    setLeftMonth(date);
    // If left matches or passes right, push right forward
    if (isSameMonth(date, rightMonth) || isBefore(rightMonth, date)) {
      setRightMonth(addMonths(date, 1));
    }
  };

  const handleRightMonthChange = (date: Date) => {
    setRightMonth(date);
    // If right matches or is before left, push left backward
    if (isSameMonth(date, leftMonth) || isBefore(date, leftMonth)) {
      setLeftMonth(addMonths(date, -1));
    }
  };

  const handleClear = () => {
    onChange({ from: undefined, to: undefined });
    setActiveView("calendar");
  };

  const handleApply = () => {
    setIsOpen(false);
    setActiveView("calendar");
  };

  // Presets
  const presets = [
    {
      label: "Today",
      getValue: () => {
        const now = new Date();
        const from = new Date(now);
        from.setHours(0, 0, 0, 0);
        const to = new Date(now);
        to.setHours(23, 59, 59, 999);
        return { from, to };
      },
    },
    {
      label: "Yesterday",
      getValue: () => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        const from = new Date(d);
        from.setHours(0, 0, 0, 0);
        const to = new Date(d);
        to.setHours(23, 59, 59, 999);
        return { from, to };
      },
    },
    {
      label: "Last 7 Days",
      getValue: () => {
        const to = new Date();
        to.setHours(23, 59, 59, 999);
        const from = new Date();
        from.setDate(from.getDate() - 6);
        from.setHours(0, 0, 0, 0);
        return { from, to };
      },
    },
    {
      label: "Last 30 Days",
      getValue: () => {
        const to = new Date();
        to.setHours(23, 59, 59, 999);
        const from = new Date();
        from.setDate(from.getDate() - 29);
        from.setHours(0, 0, 0, 0);
        return { from, to };
      },
    },
    {
      label: "This Month",
      getValue: () => {
        const now = new Date();
        const from = new Date(now.getFullYear(), now.getMonth(), 1);
        const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        to.setHours(23, 59, 59, 999);
        return { from, to };
      },
    },
  ];

  const handlePresetSelect = (preset: {
    getValue: () => { from: Date; to: Date };
  }) => {
    const { from, to } = preset.getValue();
    onChange({ from: from.toISOString(), to: to.toISOString() });
  };

  // Display text for trigger
  const displayText = React.useMemo(() => {
    if (!fromDate && !toDate) return placeholder;
    const startStr = fromDate
      ? includeTime
        ? format(fromDate, "yyyy-MM-dd HH:mm")
        : format(fromDate, "yyyy-MM-dd")
      : "Start";
    const endStr = toDate
      ? includeTime
        ? format(toDate, "yyyy-MM-dd HH:mm")
        : format(toDate, "yyyy-MM-dd")
      : "End";
    return `${startStr} ~ ${endStr}`;
  }, [fromDate, toDate, includeTime, placeholder]);

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (!open) setActiveView("calendar");
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={disabled}
            className={cn(
              // Flat like a field — same reasoning as date-picker.tsx.
              "w-full justify-start text-left font-normal shadow-none",
              !value?.from && !value?.to && "text-placeholder",
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            <span>{displayText}</span>
            {(value?.from || value?.to) && (
              <div
                className="ml-auto flex items-center"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
              >
                <X className="h-4 w-4 opacity-50 hover:opacity-100" />
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="flex flex-col sm:flex-row">
            {/* Sidebar */}
            <div className="border-b sm:border-b-0 sm:border-r border-border p-2 flex flex-col gap-1 min-w-[120px]">
              {presets.map((preset) => (
                <Button
                  key={preset.label}
                  variant="ghost"
                  size="sm"
                  className="justify-start text-xs font-normal h-8"
                  onClick={() => handlePresetSelect(preset)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>

            {/* Main Content */}
            <div className="flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-border p-3 bg-muted/10">
                <div className="flex items-center gap-2 text-sm">
                  <div
                    className={cn(
                      "cursor-pointer px-2 py-1 rounded hover:bg-accent",
                      activeView === "calendar" && "font-semibold",
                    )}
                    onClick={() => setActiveView("calendar")}
                  >
                    {formatDate(fromDate)}
                  </div>
                  {includeTime && (
                    <div
                      className={cn(
                        "cursor-pointer px-2 py-1 rounded hover:bg-accent flex items-center gap-1",
                        activeView === "time-start" &&
                          "bg-primary text-primary-foreground",
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveView("time-start");
                      }}
                    >
                      <Clock className="h-3 w-3" />
                      {formatTime(fromDate)}
                    </div>
                  )}
                  <span className="text-muted-foreground">~</span>
                  <div
                    className={cn(
                      "cursor-pointer px-2 py-1 rounded hover:bg-accent",
                      activeView === "calendar" && "font-semibold",
                    )}
                    onClick={() => setActiveView("calendar")}
                  >
                    {formatDate(toDate)}
                  </div>
                  {includeTime && (
                    <div
                      className={cn(
                        "cursor-pointer px-2 py-1 rounded hover:bg-accent flex items-center gap-1",
                        activeView === "time-end" &&
                          "bg-primary text-primary-foreground",
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveView("time-end");
                      }}
                    >
                      <Clock className="h-3 w-3" />
                      {formatTime(toDate)}
                    </div>
                  )}
                </div>
              </div>

              {/* Body */}
              <div className="p-0">
                {activeView === "calendar" && (
                  <div className="flex flex-col sm:flex-row border-collapse">
                    <div className="p-3 border-b sm:border-b-0 sm:border-r border-border">
                      <Calendar
                        mode="range"
                        selected={{ from: fromDate, to: toDate }}
                        onSelect={handleCalendarSelect}
                        disabled={(date) => {
                          if (disableFuture && date > new Date()) return true;
                          if (disablePast && date < new Date()) return true;
                          return false;
                        }}
                        month={leftMonth}
                        onMonthChange={handleLeftMonthChange}
                        numberOfMonths={1}
                        showOutsideDays={false}
                        initialFocus
                      />
                    </div>
                    <div className="p-3">
                      <Calendar
                        mode="range"
                        selected={{ from: fromDate, to: toDate }}
                        onSelect={handleCalendarSelect}
                        disabled={(date) => {
                          if (disableFuture && date > new Date()) return true;
                          if (disablePast && date < new Date()) return true;
                          return false;
                        }}
                        month={rightMonth}
                        onMonthChange={handleRightMonthChange}
                        numberOfMonths={1}
                        showOutsideDays={false}
                      />
                    </div>
                  </div>
                )}
                {activeView === "time-start" && (
                  <div className="p-3 w-full flex justify-center">
                    <TimePicker
                      value={fromDate}
                      onChange={(d) => handleTimeChange("from", d)}
                      className="w-[280px]"
                    />
                  </div>
                )}
                {activeView === "time-end" && (
                  <div className="p-3 w-full flex justify-center">
                    <TimePicker
                      value={toDate}
                      onChange={(d) => handleTimeChange("to", d)}
                      className="w-[280px]"
                    />
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-border p-3 flex justify-end gap-2 bg-muted/10">
                <Button variant="ghost" size="sm" onClick={handleClear}>
                  Clear
                </Button>
                <Button size="sm" onClick={handleApply}>
                  OK
                </Button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
